import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'

interface ImpersonationState {
  orgId: string
  orgName: string
  orgSlug: string
  /** Optional — if provided, the sidebar Plan badge reflects the impersonated org's tier. */
  subscriptionTier?: string
  /** True when a franchise admin is viewing one of their own client orgs
      (server-validated parent link) rather than a platform admin. */
  byFranchiseAdmin?: boolean
}

interface ImpersonationContextValue {
  isImpersonating: boolean
  impersonatedOrg: ImpersonationState | null
  startImpersonation: (orgId: string, orgName: string, orgSlug: string, subscriptionTier?: string, byFranchiseAdmin?: boolean) => void
  stopImpersonation: () => void
}

const STORAGE_KEY = 'app_impersonation'

const ImpersonationContext = createContext<ImpersonationContextValue>({
  isImpersonating: false,
  impersonatedOrg: null,
  startImpersonation: () => {},
  stopImpersonation: () => {}
})

/**
 * Peek at the logged-in agent from localStorage without mounting a second
 * useAuth instance. Used only to decide whether to apply impersonation state.
 */
function peekIsPlatformAdmin(): boolean {
  try {
    const stored = localStorage.getItem('app_agent_data')
    if (!stored) return false
    return Boolean(JSON.parse(stored)?.isPlatformAdmin)
  } catch {
    return false
  }
}

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [impersonatedOrg, setImpersonatedOrg] = useState<ImpersonationState | null>(() => {
    // On mount, honor stored impersonation state for platform admins, and for
    // franchise admins viewing their own clients (the server re-validates the
    // parent link on every request — this check is UX hygiene, not authz).
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      const parsed = stored ? JSON.parse(stored) : null
      if (!parsed) return null
      if (!peekIsPlatformAdmin() && !parsed.byFranchiseAdmin) {
        localStorage.removeItem(STORAGE_KEY)
        return null
      }
      return parsed
    } catch {
      return null
    }
  })

  useEffect(() => {
    if (impersonatedOrg) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(impersonatedOrg))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [impersonatedOrg])

  // Also listen for auth-data changes (login from another tab, or profile
  // update that downgraded from platform admin). If the user is no longer a
  // platform admin, clear any active impersonation overlay.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'app_agent_data' && !peekIsPlatformAdmin()) {
        setImpersonatedOrg(prev => (prev?.byFranchiseAdmin ? prev : null))
      }
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  // Sync impersonation state across tabs via storage events
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        if (e.newValue) {
          try {
            setImpersonatedOrg(JSON.parse(e.newValue))
          } catch {
            setImpersonatedOrg(null)
          }
        } else {
          setImpersonatedOrg(null)
        }
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const startImpersonation = useCallback((orgId: string, orgName: string, orgSlug: string, subscriptionTier?: string, byFranchiseAdmin?: boolean) => {
    setImpersonatedOrg({ orgId, orgName, orgSlug, subscriptionTier, byFranchiseAdmin })
    // The effective org changed — re-resolve the brand (a root admin viewing a
    // franchise/client picks up that org's brand via /api/brand/me).
    window.dispatchEvent(new Event('app:brand-may-have-changed'))
  }, [])

  const stopImpersonation = useCallback(() => {
    setImpersonatedOrg(null)
    window.dispatchEvent(new Event('app:brand-may-have-changed'))
  }, [])

  return (
    <ImpersonationContext.Provider value={{
      isImpersonating: !!impersonatedOrg,
      impersonatedOrg,
      startImpersonation,
      stopImpersonation
    }}>
      {children}
    </ImpersonationContext.Provider>
  )
}

export function useImpersonation() {
  return useContext(ImpersonationContext)
}
